import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useState, useRef, useEffect } from 'react';

/**
 * useIntersection
 *
 * @export
 * @param {Object} [options] possible additional settings
 * @param {Boolean} [options.once] set to true to stop watching after first intersection event
 * @param {String|Number} [options.pageId] id of the current page (for SPA);
 *   if provided, `once: true` will have effect within current page only (instead of entire session)
 * @param {Object} [options.root] the element that is used as the viewport for checking visiblity of the target
 * @param {String} [options.rootMargin] margin around the root. Can have values similar to the CSS margin property
 * @param {Number} [options.threshold] either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed
 * @param {Boolean} [options.defaultValue] used to prevent unwanted return when component isnt rendered yet
 * @returns { isIntersecting: boolean, setObservable: Function }
 */

function useIntersection() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      pageId = _ref.pageId,
      _ref$once = _ref.once,
      once = _ref$once === void 0 ? false : _ref$once,
      _ref$root = _ref.root,
      root = _ref$root === void 0 ? null : _ref$root,
      _ref$rootMargin = _ref.rootMargin,
      rootMargin = _ref$rootMargin === void 0 ? '0px' : _ref$rootMargin,
      _ref$threshold = _ref.threshold,
      threshold = _ref$threshold === void 0 ? 0.0 : _ref$threshold,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? false : _ref$defaultValue;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      observable = _useState2[0],
      setObservable = _useState2[1];

  var _useState3 = useState(defaultValue),
      _useState4 = _slicedToArray(_useState3, 2),
      isIntersecting = _useState4[0],
      setIsIntersecting = _useState4[1];

  var observer = useRef(null);
  useEffect(function () {
    if (!observable) {
      return function () {};
    }

    if (observer.current) {
      observer.current.disconnect();
    } else {
      observer.current = new IntersectionObserver(function useIntersectionCallback(entries) {
        var _entries$;

        if ((_entries$ = entries[0]) !== null && _entries$ !== void 0 && _entries$.isIntersecting) {
          setIsIntersecting(true);

          if (once && observer.current) {
            observer.current.disconnect();
          }
        } else if (isIntersecting) {
          /**
           * If an observed element is no longer intersecting, update the state value to false.
           *
           * Setting isIntersecting to false only when it has been previously set to true helps
           * prevent doing unnecessary state updates when the IntersectionObserver is first
           * initialized. IntersectionObserver will be fired for all observed elements once they
           * are registered, even if they are not intersecting.
           */
          setIsIntersecting(false);
        }
      }, {
        root: root,
        rootMargin: rootMargin,
        threshold: threshold
      });
    }

    observer.current.observe(observable);
    return function () {
      observer.current.disconnect();
    };
  },
  /**
   * Omitting `isIntersecting`, 'once', 'root', 'rootMargin', and 'threshold' from list of
   * dependencies. This hook should only run once per pageId to prevent disconnecting and
   * reinitializing IntersectionObserver on the same page and elements. These properties are only
   * used within the IntersectionObserver callback.
   */
  [observable, pageId] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return {
    isIntersecting: isIntersecting,
    setObservable: setObservable
  };
}

export { useIntersection as default };
